import { ButtonUtils, FontUtils, Image3D, ImgButton, LayoutUtils, MeshUtils, ObjectBuilder, Rectangle3D } from "@dreamcam/babylon-tools";
import { MessageContainer } from "./MessageContainer";
import { BroadcastApiStatus, BroadcastManagerState as BMState } from "@dreamcam/web-sdk";
import { Env } from "../../settings/Env";
import { Container } from "@babylonjs/gui/2D/controls/container.js";
import { Control } from "@babylonjs/gui/2D/controls/control.js";
import { StackPanel } from "@babylonjs/gui/2D/controls/stackPanel.js";
import { text } from "../../TextResources";
import { AppView } from "../AppView";
import { SubmenuHandlers } from "./SubmenuHandlers";
import { SubmenuVolume } from "./customComponents/SubmenuVolume";
import { Settings } from "../../settings/Settings";
import { FeatureManager, FeatureName } from "../../settings/Features";
import { MessageType } from "../../interfaces/NotificationCenter";
var NavigationPanelState;
(function (NavigationPanelState) {
    NavigationPanelState[NavigationPanelState["Navigation"] = 0] = "Navigation";
    NavigationPanelState[NavigationPanelState["Message"] = 1] = "Message";
})(NavigationPanelState || (NavigationPanelState = {}));
const StateToStatus = {};
StateToStatus[BMState.UNINITIALIZED] = BroadcastApiStatus.OFFLINE;
StateToStatus[BMState.OFFLINE] = BroadcastApiStatus.OFFLINE;
StateToStatus[BMState.AWAY] = BroadcastApiStatus.AWAY;
StateToStatus[BMState.PUBLIC] = BroadcastApiStatus.PUBLIC;
StateToStatus[BMState.PRIVATE_SHOW] = BroadcastApiStatus.PRIVATE;
StateToStatus[BMState.MY_PRIVATE_SHOW] = BroadcastApiStatus.PRIVATE;
StateToStatus[BMState.SPY_SHOW] = BroadcastApiStatus.PRIVATE;
export class BroadcastMenuNavigation {
    get isVisible() {
        return this._menuMesh.mesh.isVisible;
    }
    set isVisible(value) {
        if (this.isVisible === value)
            return;
        MeshUtils.setVisibility(this._menuMesh, value);
    }
    constructor(menuMesh, submenuVolumeMesh, submenuStreamsMesh, 
    // submenuClientProfileMesh: Mesh2D,
    menuFunctions, callbacks, subtitleController) {
        // private readonly _submenuClientProfile: SubmenuClientProfile;
        this._streamsButton = null;
        this._passthroughButton = null;
        this._streamsMenuControls = new Map();
        this._muteSoundButton = undefined;
        this._subtitlesButton = undefined;
        this._isSyncingSubtitlesButton = false;
        this._broadcastState = BMState.UNINITIALIZED;
        this._broadcastStreams = [];
        this._broadcastAvailableStreams = [];
        this._currentStream = null;
        this._relatedButton = null;
        this._toggleMute = () => {
            this._submenuVolume.mute(!this._callbacks.getPlayerSoundMuted());
        };
        this._onMuteButtonEnter = () => {
            this._streamsSubmenuHandlers.hideSubmenuInstantly();
            this._submenuVolume.submenuHandlers.hideSubmenuCancel();
            if (this.isVisible)
                this._submenuVolume.submenuHandlers.showSubmenu(SubmenuHandlers.longLifetime);
        };
        this._onMuteButtonOut = () => {
            this._submenuVolume.submenuHandlers.hideSubmenuCancel();
            this._submenuVolume.submenuHandlers.hideSubmenuStart(SubmenuHandlers.shortLifetime);
        };
        this._loadPreviousBroadcast = () => {
            this._loadNeighbourBroadcast("previous");
        };
        this._loadNextBroadcast = () => {
            this._loadNeighbourBroadcast("next");
        };
        this._loadLobby = () => {
            if (this._canLeaveBroadcast(this._broadcastState)) {
                (async () => this._callbacks.openLobbyAsync())();
            }
        };
        this.canLeaveBroadcast = () => {
            return this._canLeaveBroadcast(this._broadcastState);
        };
        this._canLeaveBroadcast = (state) => {
            if (state === BMState.MY_PRIVATE_SHOW || state === BMState.SPY_SHOW) {
                this._messagePanel.textBlock.text = this._broadcastState === BMState.MY_PRIVATE_SHOW
                    ? text.broadcast_menu_privateShow_leaveWarning
                    : text.broadcast_menu_spyMode_leaveWarning;
                this._showPanel(NavigationPanelState.Message);
                setTimeout(() => this._showPanel(NavigationPanelState.Navigation), Env.broadcastMenuMessageLifetimeInMillis);
                return false;
            }
            if (this._passthroughButton)
                this._passthroughButton.isToggled = false;
            return true;
        };
        this._menuMesh = menuMesh;
        this._submenuStreamsMesh = submenuStreamsMesh;
        this._menuFunctions = menuFunctions;
        this._callbacks = callbacks;
        this._subtitleController = subtitleController;
        this._streamsSubmenuHandlers = new SubmenuHandlers(this._submenuStreamsMesh);
        this._submenuVolume = new SubmenuVolume(submenuVolumeMesh, {
            getMuted: () => this._callbacks.getPlayerSoundMuted(),
            setMuted: (muted) => this._callbacks.setPlayerSoundMuted(muted),
            getVolume: () => this._callbacks.getPlayerSoundVolume(),
            setVolume: (volume) => this._callbacks.setPlayerSoundVolume(volume),
        }, {
            update: (muted) => {
                const sb = Env.broadcast.menu.navigation.leftButtonGroup.soundButton;
                this._muteSoundButton?.changeLayout(muted
                    ? sb.disabledImageLayout : sb.enabledImageLayout);
            },
        });
        // this._submenuClientProfile = new SubmenuClientProfile(submenuClientProfileMesh, {
        //     signout: async () => {
        //         this._submenuClientProfile.submenuHandlers.hideSubmenuInstantly();
        //         if (this._canLeaveBroadcast(this._broadcastState)) {
        //             await AppView.getInstance().appController.sdk.authManager.logoutAsync();
        //             if (AppView.getInstance().isApiAuth) {
        //                 await AppView.getInstance().logoutApiAuthAsync!();
        //             } else if (AppView.getInstance().logoutAsync) {
        //                 await AppView.getInstance().logoutAsync!();
        //             }
        //         }
        //     },
        // });
        const menu = Env.broadcast.menu;
        const cornerRadius = this._menuMesh.m2px(menu.cornerRadius);
        const navigationPanelContainer = new Rectangle3D("navigationPanelContainer");
        navigationPanelContainer.widthInPixels = this._menuMesh.m2px(menu.width);
        navigationPanelContainer.heightInPixels = this._menuMesh.m2px(menu.navigation.height);
        navigationPanelContainer.cornerRadius = cornerRadius;
        navigationPanelContainer.background = menu.backgroundColor;
        navigationPanelContainer.thickness = 0;
        menuMesh.texture.addControl(navigationPanelContainer);
        this._navigationPanel = this._addNavigationPanel(navigationPanelContainer);
        this._menuFunctions.updateMenuPosition();
        const msg = JSON.parse(JSON.stringify(Env.broadcast.menu.messagePanel));
        msg.height = menu.navigation.height;
        this._messagePanel = new MessageContainer("navigationPanelMessageContainer", "", msg, this._menuMesh);
        this._messagePanel.isVisible = false;
        navigationPanelContainer.addControl(this._messagePanel);
        this._prepareSubmenuStreams();
    }
    show() {
        const sb = Env.broadcast.menu.navigation.leftButtonGroup.soundButton;
        this._muteSoundButton.changeLayout(Settings.getInstance().broadcast.isSoundMuted ? sb.disabledImageLayout : sb.enabledImageLayout);
        this._submenuVolume.refreshVolume();
        this._updatePassthroughButton();
        this._syncSubtitleButton();
    }
    _addNavigationPanel(parent) {
        const menu = Env.broadcast.menu;
        const backgroundPanel = new Container("navigationPanelContainer");
        backgroundPanel.widthInPixels = this._menuMesh.m2px(menu.width);
        backgroundPanel.heightInPixels = this._menuMesh.m2px(menu.navigation.height);
        parent.addControl(backgroundPanel);
        const lg = Env.broadcast.menu.navigation.leftButtonGroup;
        const rg = Env.broadcast.menu.navigation.rightButtonGroup;
        const leftStackPanel = new StackPanel("navigationPanelLeftButtons");
        leftStackPanel.isVertical = false;
        leftStackPanel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        leftStackPanel.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
        leftStackPanel.heightInPixels = this._menuMesh.m2px(lg.soundButton.height);
        leftStackPanel.leftInPixels = this._menuMesh.m2px(lg.left);
        leftStackPanel.spacing = this._menuMesh.m2px(lg.spacing);
        backgroundPanel.addControl(leftStackPanel);
        {
            this._muteSoundButton = new ImgButton("navigationPanelSoundButton", this._callbacks.getPlayerSoundMuted()
                ? lg.soundButton.disabledImageLayout : lg.soundButton.enabledImageLayout);
            this._muteSoundButton.widthInPixels = this._menuMesh.m2px(lg.soundButton.width);
            this._muteSoundButton.heightInPixels = this._menuMesh.m2px(lg.soundButton.height);
            this._clearDetectPointerOnOpaqueOnly(this._muteSoundButton);
            this._muteSoundButton.onClickObserver.add(this._toggleMute);
            this._muteSoundButton.onPointerEnterObservable.add(this._onMuteButtonEnter);
            this._muteSoundButton.onPointerOutObservable.add(this._onMuteButtonOut);
            leftStackPanel.addControl(this._muteSoundButton);
            this._streamsButton = new ImgButton("navigationPanelStreamsButton", lg.streamsButton.imageLayout3D);
            this._streamsButton.widthInPixels = this._menuMesh.m2px(lg.streamsButton.width);
            this._streamsButton.heightInPixels = this._menuMesh.m2px(lg.streamsButton.height);
            this._clearDetectPointerOnOpaqueOnly(this._streamsButton);
            this._streamsButton.onPointerEnterObservable.add(() => {
                if (this.isVisible) {
                    this._submenuVolume.submenuHandlers.hideSubmenuInstantly();
                    this._streamsSubmenuHandlers.showSubmenu(SubmenuHandlers.longLifetime);
                }
            });
            leftStackPanel.addControl(this._streamsButton);
            if (this._subtitleController) {
                const sb = lg.subtitlesButton;
                this._subtitlesButton = ButtonUtils.CreateImgToggleButton("navigationPanelSubtitlesButton", sb, this._menuMesh);
                this._clearDetectPointerOnOpaqueOnly(this._subtitlesButton);
                this._subtitlesButton.onIsToggledChangedObservable.add(async (isToggled) => {
                    if (this._isSyncingSubtitlesButton)
                        return;
                    const controller = this._subtitleController;
                    if (!controller)
                        return;
                    const wantOn = isToggled;
                    const isOn = controller.state.isOn;
                    if (wantOn !== isOn)
                        await controller.toggle();
                });
                let isSendingAnalytics = false;
                this._subtitlesButton.onPointerClickObservable.add(async () => {
                    try {
                        if (isSendingAnalytics)
                            return;
                        isSendingAnalytics = true;
                        const sdk = AppView._instance.appController.sdk;
                        const body = {
                            event_name: "stream_model_room_subtitles_toggle",
                            event_context: "webxr_client",
                            event_ts: new Date().toISOString(),
                            event_data: {
                                is_toggled: !this._subtitlesButton?.isToggled,
                                model_id: sdk.broadcastManager.broadcast?.modelId,
                                model_nickname: sdk.broadcastManager.broadcast?.modelNickname,
                                broadcast_status: sdk.broadcastManager.broadcast?.broadcastStatus,
                                is_logged_in: sdk.authManager.isAuthorized,
                                client_nickname: sdk.authManager.authResponse?.clientNickname,
                                broadcast_id: sdk.broadcastManager.broadcastId,
                                partner_client_id: sdk.authManager.authResponse?.clientId,
                                bss_url: sdk._dataProvider?._baseUrl,
                                partner_id: sdk.sessionContext?.partnerId,
                            }
                        };
                        const abortController = new AbortController();
                        const init = {
                            method: "POST",
                            body: JSON.stringify(body),
                            headers: [["X-Measurement-ID", "DC-9M2X8R0T6V"]],
                            signal: abortController.signal
                        };
                        const timeout = setTimeout(() => abortController.abort(), 10_000);
                        await fetch("https://click.dreamcamtrue.com", init);
                        clearTimeout(timeout);
                    }
                    catch { }
                    finally {
                        isSendingAnalytics = false;
                    }
                });
                leftStackPanel.addControl(this._subtitlesButton);
                this._subtitleController.subscribe({
                    onStateChanged: () => this._syncSubtitleButton(),
                });
                this._syncSubtitleButton();
            }
            if (FeatureManager.instance.isFeatureRequested(FeatureName.Passthrough)) {
                this._passthroughButton = ButtonUtils.CreateImgToggleButton("navigationPanelPassthroughButton", lg.passthroughButton, this._menuMesh);
                leftStackPanel.addControl(this._passthroughButton);
                if (FeatureManager.instance.isFeatureSupported(FeatureName.Passthrough)) {
                    const player = this._callbacks.getPlayer();
                    this._passthroughButton.onIsToggledChangedObservable.add(async (isPassthroughEnabled) => {
                        FeatureManager.instance.setFeatureEnabled(FeatureName.Passthrough, isPassthroughEnabled);
                        await AppView.getInstance().appController.environmentController.setRoomEnvironmentAsync(AppView.getInstance().environmentView);
                        if (player) {
                            if (player.videoTracks.length > 1)
                                player.videoTracks.selectedIndex = player.videoTracks.findIndex(t => t.label === (isPassthroughEnabled ? "Passthrough" : "Main"));
                            player.includeMask = isPassthroughEnabled; //due to a player bug setting `includeMask` doesn't trigger the `trackchanged` event, so we are selecting the track manually until it's fixed
                            if (isPassthroughEnabled)
                                player.passthroughToken = this._callbacks.getPassthroughToken();
                        }
                    });
                    player?.addEventListener("videotrackchange", () => {
                        const isOn = player.videoTracks.find(t => t.selected)?.kind === "alternative";
                        if (this._passthroughButton && this._passthroughButton.isToggled !== isOn)
                            this._passthroughButton.isToggled = isOn;
                        this._updatePassthroughButton();
                    });
                    player?.addEventListener("streamtypechange", () => {
                        const isOn = player.videoTracks.find(t => t.selected)?.kind === "alternative";
                        if (this._passthroughButton && this._passthroughButton.isToggled !== isOn)
                            this._passthroughButton.isToggled = isOn;
                        this._updatePassthroughButton();
                    });
                }
                else {
                    this._passthroughButton.onIsToggledChangedObservable.add(async (isPassthroughEnabled) => {
                        if (isPassthroughEnabled) {
                            this._passthroughButton.isToggled = false;
                            AppView.getInstance().appController.notificationCenter?.showMessage(MessageType.error, text.common_feature_pt_error_unavailable);
                        }
                    });
                }
            }
        }
        const rightStackPanel = new StackPanel("navigationPanelRightButtons");
        rightStackPanel.isVertical = false;
        rightStackPanel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_RIGHT;
        rightStackPanel.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
        rightStackPanel.heightInPixels = this._menuMesh.m2px(lg.soundButton.height);
        rightStackPanel.leftInPixels = -this._menuMesh.m2px(rg.right);
        rightStackPanel.spacing = this._menuMesh.m2px(rg.spacing);
        backgroundPanel.addControl(rightStackPanel);
        {
            const lobbyButton = new ImgButton("lobbyButton", rg.lobbyButton.imageLayout);
            lobbyButton.widthInPixels = this._menuMesh.m2px(rg.lobbyButton.width);
            lobbyButton.heightInPixels = this._menuMesh.m2px(rg.lobbyButton.height);
            this._clearDetectPointerOnOpaqueOnly(lobbyButton);
            lobbyButton.onClickObserver.add(this._loadLobby);
            rightStackPanel.addControl(lobbyButton);
            this._relatedButton = new ImgButton("relatedButton", rg.relatedButton.disabledImageLayout);
            this._relatedButton.widthInPixels = this._menuMesh.m2px(rg.relatedButton.width);
            this._relatedButton.heightInPixels = this._menuMesh.m2px(rg.relatedButton.height);
            this._clearDetectPointerOnOpaqueOnly(this._relatedButton);
            this._relatedButton.onClickObserver.add(() => {
                this._menuFunctions.setRelatedPanelVisibility(!this._menuFunctions.getRelatedPanelVisibility());
                this._menuFunctions.updateRelatedButton();
            });
            rightStackPanel.addControl(this._relatedButton);
            const prevButton = new ImgButton("prevButton", rg.previousBroadcastButton.imageLayout);
            prevButton.widthInPixels = this._menuMesh.m2px(rg.previousBroadcastButton.width);
            prevButton.heightInPixels = this._menuMesh.m2px(rg.previousBroadcastButton.height);
            this._clearDetectPointerOnOpaqueOnly(prevButton);
            prevButton.onClickObserver.add(this._loadPreviousBroadcast);
            rightStackPanel.addControl(prevButton);
            const nextButton = new ImgButton("nextButton", rg.nextBroadcastButton.imageLayout);
            nextButton.widthInPixels = this._menuMesh.m2px(rg.nextBroadcastButton.width);
            nextButton.heightInPixels = this._menuMesh.m2px(rg.nextBroadcastButton.height);
            this._clearDetectPointerOnOpaqueOnly(nextButton);
            nextButton.onClickObserver.add(this._loadNextBroadcast);
            rightStackPanel.addControl(nextButton);
        }
        return backgroundPanel;
    }
    _clearDetectPointerOnOpaqueOnly(imgButton) {
        const child0 = imgButton.children[0];
        if (child0 instanceof Image3D) {
            child0.detectPointerOnOpaqueOnly = false;
        }
    }
    _syncSubtitleButton() {
        if (!this._subtitlesButton || !this._subtitleController)
            return;
        const state = this._subtitleController.computeButtonState();
        const button = this._subtitlesButton;
        this._isSyncingSubtitlesButton = true;
        button.isVisible = state !== "hidden";
        switch (state) {
            case "hidden":
                break;
            case "inactive":
                button.isEnabled = false;
                button.isToggled = false;
                break;
            case "off":
                button.isEnabled = true;
                button.isToggled = false;
                break;
            case "on":
                button.isEnabled = true;
                button.isToggled = true;
                break;
        }
        this._isSyncingSubtitlesButton = false;
    }
    _loadNeighbourBroadcast(direction) {
        if (this._canLeaveBroadcast(this._broadcastState)) {
            this._menuFunctions.setRelatedPanelVisibility(false);
            this._menuFunctions.updateRelatedButton();
            this._menuFunctions.savePanelsVisibility();
            this._menuFunctions.setPrivateShowPanelVisibility(false);
            if (direction === "previous") {
                this._callbacks.loadPreviousBroadcast();
            }
            else {
                this._callbacks.loadNextBroadcast();
            }
        }
    }
    ;
    stateChanged(state, _isGuest, _reason) {
        this._broadcastState = state;
        this._updateStreamsMenu();
        this._updatePassthroughButton();
    }
    _updatePassthroughButton() {
        if (this._passthroughButton === null)
            return;
        switch (this._broadcastState) {
            case BMState.UNINITIALIZED:
            case BMState.OFFLINE:
            case BMState.AWAY:
                this._passthroughButton.isVisible = false;
                this._passthroughButton.isEnabled = this._currentStream?.hasMask ?? false;
                break;
            case BMState.PUBLIC:
            case BMState.MY_PRIVATE_SHOW:
            case BMState.SPY_SHOW:
                this._passthroughButton.isVisible = true;
                this._passthroughButton.isEnabled = this._currentStream?.hasMask ?? false;
                break;
            case BMState.PRIVATE_SHOW:
                this._passthroughButton.isVisible = true;
                this._passthroughButton.isEnabled = false;
                break;
        }
    }
    outputAvailableStreams(availableStreams, allStreams) {
        this._broadcastStreams = allStreams;
        this._broadcastAvailableStreams = availableStreams;
        this._updateStreamsMenu();
        this._updatePassthroughButton();
    }
    outputCurrentStream(streamInfo) {
        this._currentStream = streamInfo;
        for (let streamType of this._streamsMenuControls.keys()) {
            const controls = this._streamsMenuControls.get(streamType);
            if (controls === undefined)
                continue;
            controls.checkboxImage.isVisible = streamInfo !== null && streamInfo.streamType === streamType;
        }
        if (this._streamsButton !== null) {
            this._streamsButton.isVisible = streamInfo !== null;
            // this._streamsButton.isEnabled = streamInfo !== null;
            if (streamInfo !== null) {
                if (this._streamsMenuControls.get("video2D")?.checkboxImage.isVisible === true) {
                    this._streamsButton.changeLayout(Env.broadcast.menu.navigation.leftButtonGroup.streamsButton.imageLayout2D);
                }
                else if (this._streamsMenuControls.get("video3D")?.checkboxImage.isVisible === true) {
                    if (AppView.getIs6kStream(streamInfo)) {
                        this._streamsButton.changeLayout(Env.broadcast.menu.navigation.leftButtonGroup.streamsButton.imageLayout3D6K);
                    }
                    else {
                        this._streamsButton.changeLayout(Env.broadcast.menu.navigation.leftButtonGroup.streamsButton.imageLayout3D);
                    }
                }
            }
        }
        this._updatePassthroughButton();
    }
    _updateStreamsMenu() {
        for (let streamType of this._streamsMenuControls.keys()) {
            const controls = this._streamsMenuControls.get(streamType);
            if (controls === undefined)
                continue;
            const isAvailable = this._broadcastAvailableStreams.find(x => x.streamType === streamType) !== undefined;
            const is6K = this._broadcastStreams.find(x => x.streamType === streamType &&
                AppView.getIs6kStreamInTheRoom("video3D", StateToStatus[this._broadcastState], x));
            setTimeout(() => {
                controls.streamItem.isEnabled = isAvailable;
                if (streamType === "video3D") {
                    controls.textBlock.text = is6K ? text.broadcast_menu_navigation_stream_3D_6K : text.broadcast_menu_navigation_stream_3D;
                }
                else {
                    controls.textBlock.text = text.broadcast_menu_navigation_stream_2D;
                }
            }, 0);
        }
    }
    clear() {
        this._currentStream = null;
        if (this._passthroughButton)
            this._passthroughButton.isToggled = false;
    }
    _showPanel(state) {
        this._navigationPanel.isVisible = state === NavigationPanelState.Navigation;
        this._messagePanel.isVisible = state === NavigationPanelState.Message;
    }
    _prepareSubmenuStreams() {
        const mesh = this._submenuStreamsMesh;
        MeshUtils.setVisibility(mesh, false);
        const menu = Env.broadcast.menu;
        const str = menu.navigation.submenus.streams;
        const onPointerEnterHandler = () => {
            this._streamsSubmenuHandlers.hideSubmenuCancel();
        };
        const onPointerOutHandler = () => {
            this._streamsSubmenuHandlers.hideSubmenuStart(SubmenuHandlers.shortLifetime);
        };
        const root = new Rectangle3D("streamsRoot");
        root.thickness = 0;
        root.background = menu.backgroundColor;
        root.cornerRadius = mesh.m2px(menu.cornerRadius);
        root.onPointerEnterObservable.add(onPointerEnterHandler);
        root.onPointerOutObservable.add(onPointerOutHandler);
        mesh.texture.addControl(root);
        const stackPanel = new StackPanel("streamsSubmenu");
        stackPanel.isHitTestVisible = false;
        stackPanel.isVertical = true;
        stackPanel.widthInPixels = mesh.m2px(str.width);
        root.addControl(stackPanel);
        const createStreamItem = (streamType) => {
            const streamItem = ButtonUtils.CreateImgButton(`streamItem_${streamType}`, str.item.colorLayout, mesh);
            streamItem.onPointerEnterObservable.add(onPointerEnterHandler);
            streamItem.onPointerOutObservable.add(onPointerOutHandler);
            streamItem.onClickObserver.add(() => {
                setTimeout(() => this._streamsSubmenuHandlers.hideSubmenuInstantly(), 0);
                this._callbacks.setPreferredStreamType(streamType);
            });
            const checkboxImage = new Image3D(`checkboxImage_${streamType}`, str.item.checkbox.image);
            LayoutUtils.SetControlLayout(checkboxImage, LayoutUtils.GetLayout(str.item.checkbox, mesh));
            streamItem.addControl(checkboxImage);
            let itemCaption = "unknown";
            switch (streamType) {
                case "video2D":
                    itemCaption = text.broadcast_menu_navigation_stream_2D;
                    break;
                case "video3D":
                    itemCaption = text.broadcast_menu_navigation_stream_3D;
                    break;
            }
            const textBlock = ObjectBuilder.createTextBlock3D(mesh, `textBlock_${streamType}`, itemCaption, str.item.text.textLayout);
            textBlock.stateController.setStates(str.item.text.textLayout.states.map(s => { return { state: s.state, style: FontUtils.getAdjustedFontStyle(s.style, mesh) }; }));
            streamItem.addControl(textBlock);
            return { streamItem, checkboxImage, textBlock };
        };
        const stream3dItemControls = createStreamItem("video3D");
        stackPanel.addControl(stream3dItemControls.streamItem);
        const stream2dItemControls = createStreamItem("video2D");
        stackPanel.addControl(stream2dItemControls.streamItem);
        this._streamsMenuControls.clear();
        this._streamsMenuControls.set("video3D", stream3dItemControls);
        this._streamsMenuControls.set("video2D", stream2dItemControls);
    }
    updateRelatedButton() {
        if (this._relatedButton === null)
            return;
        const rb = Env.broadcast.menu.navigation.rightButtonGroup.relatedButton;
        this._relatedButton?.changeLayout(this._menuFunctions.getRelatedPanelVisibility()
            ? rb.enabledImageLayout : rb.disabledImageLayout);
    }
}
